import { untranslated, useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import { useState, useEffect, useCallback } from "react";
import { formUserSettingsController } from "@Actions/Forms";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { formCategoryObject } from "@Features/portal-settings/components/PortalSetings/Configuration/FormsAndFields/constants";
import { RootState } from "@Reducers/index";
import type { FormUserSettingsControllerResponse } from "@ServerTypes/forms/forms.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * `checkFormUserSettings` answers with an `isUserSettingExists` flag that the shared
 * `formUserSettingsControllerResponse` schema — a bare `{ status, result, count }` envelope —
 * does not model. Reported as a server-schema gap; read through this view type meanwhile.
 */
type CheckFormUserSettingsData = FormUserSettingsControllerResponse & { isUserSettingExists?: boolean };

/** Which destructive action the confirm dialog is currently armed for. */
interface WarningModalState {
  status: boolean;
  source?: string;
}

interface UseFromListProps {
  /**
   * Route param, branded by the caller's own `useParams`. Optional because a route need not carry
   * one, which is why both settings calls below check it before they build a request.
   */
  projectId?: ObjectIdString;
  /** A `formCategories` value or a custom module's `resourceType`. */
  formCategory: string;
  isFocused?: boolean;
  showOnlyGrid?: boolean;
}

function useFromList({ projectId, formCategory, isFocused = true, showOnlyGrid = false }: UseFromListProps) {
  const { t } = useTranslation();
  const [userSettingsExists, setUserSettingsExists] = useState(false);
  const [showWarning, setShowWarning] = useState<WarningModalState>({ status: false });
  const [moduleTxt, setModuleTxt] = useState("");

  const dispatch = useDispatch();

  const customModules = useSelector((state: RootState) => state.customModules);

  const getUserSettingsForFormConfig = useCallback(async () => {
    // `formUserSettingsControllerRequest` requires a branded 24-hex `projectId`, so without one the
    // request could only ever come back Zod-rejected — i.e. with no `isUserSettingExists`, which is
    // exactly the `false` set here. The state write is kept rather than skipped so this is the same
    // outcome the round-trip produced, minus the doomed call. (Today it is unreachable anyway: the
    // hook's single consumer, TaskInspectionAndProgressForm.tsx:218, does not destructure this
    // callback, so the only caller is the `projectId`-guarded effect below.)
    if (!projectId) {
      setUserSettingsExists(false);
      return;
    }
    const checkUserSettingExists = await formUserSettingsController({
      action: "checkFormUserSettings",
      projectId,
      resourceType: formCategory,
    });
    setUserSettingsExists(
      !!(checkUserSettingExists.data as CheckFormUserSettingsData | undefined)?.isUserSettingExists,
    );
  }, [projectId, formCategory]);

  useEffect(() => {
    if (isFocused && !showOnlyGrid && projectId) {
      getUserSettingsForFormConfig();
    }
  }, [formCategory, isFocused, projectId, showOnlyGrid, getUserSettingsForFormConfig]);

  useEffect(() => {
    if (customModules.length) {
      const categoryObj = customModules.find((module) => module.resourceType == formCategory);
      const moduleText = categoryObj?.activeCell || formCategoryObject[formCategory]?.activeCell || "";

      setModuleTxt(moduleText);
    } else {
      setModuleTxt(formCategoryObject[formCategory]?.activeCell ?? "");
    }
  }, [customModules, formCategory]);

  const showWarningModal = (source: string) => {
    setShowWarning({ status: true, source });
  };
  const hideWarningModal = () => {
    setShowWarning({ status: false });
  };

  async function resetConfig() {
    if (userSettingsExists) {
      // Same required-and-branded `projectId` as `getUserSettingsForFormConfig`: no id, no settings
      // document to delete. Unreachable in practice — `userSettingsExists` is only ever set true off
      // a check that already had one — but it takes the SAME exit the server's rejection took: the
      // confirm dialog closes and an error toast goes up. Returning bare would leave the dialog open
      // with its Reset button doing nothing.
      if (!projectId) {
        hideWarningModal();
        dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
        return;
      }
      const res = await formUserSettingsController({
        action: "deleteFormUserSettings",
        projectId,
        resourceType: formCategory,
      });
      if (res.data.status) {
        dispatch(
          showToastNotification({
            content: t("Config reseted successfully") as TranslatedString,
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        setUserSettingsExists(false);
        hideWarningModal();
      } else {
        hideWarningModal();
        dispatch(
          showToastNotification({
            // Server-authored message, so it is branded rather than translated. `errorMessage` is
            // optional on the failure branch of `statusResponse`, hence the fallback — without one
            // the toast renders an empty bubble on any failure that omits it.
            content: res.data.errorMessage ? untranslated(res.data.errorMessage) : SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } else {
      dispatch(
        showToastNotification({
          content: t("User settings do not exists") as TranslatedString,
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  }

  function resetConfigClick() {
    if (userSettingsExists) {
      showWarningModal("resetConfig");
    } else {
      dispatch(
        showToastNotification({
          content: t("User settings does not exists") as TranslatedString,
          type: TOAST_TYPE_WARNING,
        }),
      );
    }
  }

  return {
    userSettingsExists,
    resetConfig,
    getUserSettingsForFormConfig,
    showWarning,
    setShowWarning,
    showWarningModal,
    hideWarningModal,
    resetConfigClick,
    moduleTxt,
  };
}

export default useFromList;
